import _ from 'lodash';
import type { StudioPublishActions } from '../../actions/studio/publish';
import type { StagedChange } from '../../types/Publish';
import { PUBLISH_SCOPE, PublishProjectScope } from '../../types/Publish';
import logger from '../../modules/logging/logger';
import { RESET_STUDIO_ACTION } from '../consts';
import type { ObjectDefinition } from '../../types/Schema';
import { findObjectInList } from '../../modules/studio/utils-ts';
import type { InfoMessageType } from '../../components/common/InfoMessage/InfoMessage';
import type AppError from '../../lib/AppError';

export const CONTENT_STATUS_TEXT = {
    NO_CHANGES: 'No Pending Changes',
    HAS_CHANGES: 'Ready to Publish',
    PUBLISHED: 'Content Published',
    PUBLISHING: 'Publishing Content',
    SCHEDULING: 'Scheduling a Publish',
    SCHEDULED: 'Content Scheduled to be Published',
    PUBLISH_FAILED: 'Failed Publishing Content',
    SCHEDULE_FAILED: 'Failed Scheduling Content'
};

export type ScheduledActionState = 'scheduled' | 'succeeded' | 'executing' | 'cancelled';
export type ScheduledActionActionType = 'publish' | 'unpublish';
export interface ScheduledAction {
    id: string;
    srcType: string;
    srcProjectId: string;
    name: string;
    action: ScheduledActionActionType;
    state: ScheduledActionState;
    createdAt?: string;
    createdBy?: string;
    executeAt: string;
    documentIds: string[];
}

export interface State {
    publishScope: PUBLISH_SCOPE;
    publishProjectScope: PublishProjectScope;
    isPublishing: boolean;
    isScheduling: boolean;
    contentStatus: (typeof CONTENT_STATUS_TEXT)[keyof typeof CONTENT_STATUS_TEXT];
    isCheckingContentChanges: boolean;
    checkContentChangesInitiator: 'publishScopeChange' | null;
    stagedChanges: StagedChange[] | null;
    stagedChangesError: AppError | null;
    publishingError: Error | null;
    publishedChanges: ObjectDefinition[] | null;
    scheduledActions: ScheduledAction[];
    scheduledActionsError: Error | null;
    scheduledActionsObjectMap: Record<string, ScheduledAction[]>;
    deployErrorMessage: string;
    infoMessage: string;
    infoMessageType: InfoMessageType | null;
}

const getDefaultState = (): State => ({
    publishScope: PUBLISH_SCOPE.SITE,
    publishProjectScope: PublishProjectScope.All,
    isPublishing: false,
    isScheduling: false,
    publishingError: null,
    contentStatus: CONTENT_STATUS_TEXT.NO_CHANGES,
    isCheckingContentChanges: false,
    checkContentChangesInitiator: null,
    stagedChanges: null,
    stagedChangesError: null,
    publishedChanges: null,
    scheduledActions: [],
    scheduledActionsError: null,
    scheduledActionsObjectMap: {},
    deployErrorMessage: '',
    infoMessage: '',
    infoMessageType: null
});

const studioPublishReducer = (state = getDefaultState(), action: StudioPublishActions | null): State => {
    switch (action?.type) {
        case 'SET_STUDIO_PUBLISH_SCOPE': {
            return {
                ...state,
                publishScope: action.data.publishScope
            };
        }

        case 'SET_STUDIO_PUBLISH_PROJECT_SCOPE': {
            return {
                ...state,
                publishProjectScope: action.data.publishProjectScope
            };
        }

        case 'SET_STUDIO_PUBLISHING': {
            return {
                ...state,
                isPublishing: action.data.isPublishing,
                contentStatus: action.data.contentStatus
            };
        }

        case 'SET_STUDIO_SCHEDULING': {
            return {
                ...state,
                isScheduling: action.data.isScheduling,
                contentStatus: action.data.contentStatus
            };
        }

        case 'SET_STUDIO_PUBLISHED_CHANGES': {
            const { changes } = action.data || {};
            return {
                ...state,
                publishedChanges: changes
            };
        }

        case 'SET_STUDIO_PUBLISHING_ERROR': {
            return {
                ...state,
                publishingError: action.data
            };
        }

        case 'SET_HAS_CONTENT_CHANGES': {
            const { stagedChanges, error } = action.data || {};

            if (!stagedChanges) {
                return {
                    ...state,
                    stagedChanges: null,
                    stagedChangesError: error || null
                };
            }

            const prevStagedChanges = state.stagedChanges;
            const publishProjectScope = state.publishProjectScope;
            // loop over previous stagedChanges to preserve checkbox status between refetches.
            const stagedChangesWithStagingState = stagedChanges
                .filter((object) => {
                    switch (publishProjectScope) {
                        case PublishProjectScope.All:
                            return true;
                        case PublishProjectScope.Content:
                            return ['content', 'asset'].includes(object.changeType);
                        default:
                            logger.error('Error filtering publish project scope', { publishProjectScope });
                            return true;
                    }
                })
                .map((object) => {
                    const prevStagedChange = findObjectInList(prevStagedChanges ?? [], object);
                    object.isStaged = prevStagedChange?.isStaged ?? true;
                    object.updatedBy = object.updatedBy?.filter((user) => user !== 'stackbit') ?? [];
                    return object;
                });
            return {
                ...state,
                stagedChanges: stagedChangesWithStagingState,
                stagedChangesError: error || null
            };
        }

        case 'SET_STAGED_CHANGES': {
            const { changes, error } = action.data || {};
            return {
                ...state,
                stagedChanges: changes,
                stagedChangesError: error || null
            };
        }

        case 'SET_CHECKING_CONTENT_CHANGES': {
            const { isChecking, initiator } = action.data || {};
            return {
                ...state,
                checkContentChangesInitiator: initiator ?? null,
                isCheckingContentChanges: isChecking
            };
        }

        case 'FETCH_SCHEDULED_ACTIONS_SUCCEEDED': {
            const { scheduledActions, scheduledActionsObjectMap } = action.data || {};
            return {
                ...state,
                scheduledActions: scheduledActions,
                scheduledActionsObjectMap: scheduledActionsObjectMap
            };
        }
        case 'SET_STUDIO_PUBLISH_MESSAGES': {
            return {
                ...state,
                ..._.pick(action.data, ['infoMessage', 'infoMessageType', 'deployErrorMessage'])
            };
        }

        case 'FETCH_SCHEDULED_ACTIONS_STARTED': {
            return {
                ...state,
                scheduledActionsError: null
            };
        }

        case 'FETCH_SCHEDULED_ACTIONS_FAILED': {
            return {
                ...state,
                scheduledActionsError: action.data.error
            };
        }

        case RESET_STUDIO_ACTION: {
            return getDefaultState();
        }

        default: {
            return state;
        }
    }
};

export default studioPublishReducer;
